import { createContext, useContext, useMemo, useReducer } from 'react';
import defaultConfig from '../data/siteConfig';
import { createHistoryReducer, initialHistory } from './historyReducer';

const SiteConfigContext = createContext();
const SiteConfigDispatchContext = createContext();
const SiteConfigHistoryContext = createContext();

const ADMIN_CONFIG_KEY = 'fgl_admin_config';

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Minimum shape the app needs to render without crashing. Anything that fails
 * this must never become boot state.
 */
function isValidConfig(candidate) {
  if (!isPlainObject(candidate)) return false;
  if (!isPlainObject(candidate.meta)) return false;

  const theme = candidate.theme;
  if (!isPlainObject(theme)) return false;
  if (!isPlainObject(theme.colors)) return false;
  if (!isPlainObject(theme.fonts)) return false;
  if (!isPlainObject(theme.sizes)) return false;

  if (!isPlainObject(candidate.pages) || Object.keys(candidate.pages).length === 0) return false;
  if (!isPlainObject(candidate.i18n) || !isPlainObject(candidate.i18n.es)) return false;
  if (!isPlainObject(candidate.sidebar)) return false;

  return true;
}

/**
 * Objects merge key-wise so a config saved before a key existed self-heals from
 * the bundled default. Arrays (blocks, navItems, ...) replace wholesale.
 */
function deepMerge(defaults, saved) {
  if (saved === undefined) return defaults;
  if (!isPlainObject(defaults) || !isPlainObject(saved)) return saved;

  const result = { ...defaults };
  for (const key of Object.keys(saved)) {
    if (isPlainObject(defaults[key]) && isPlainObject(saved[key])) {
      result[key] = deepMerge(defaults[key], saved[key]);
    } else {
      result[key] = saved[key];
    }
  }
  return result;
}

/** Fills in anything the bundled default has gained since `candidate` was written. */
function mergeWithDefaults(candidate) {
  return deepMerge(defaultConfig, candidate);
}

function deepSet(obj, path, value) {
  const keys = path.split('.');
  const result = { ...obj };
  let current = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (Array.isArray(current[key])) {
      current[key] = [...current[key]];
    } else {
      current[key] = { ...current[key] };
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
  return result;
}

function deepGet(obj, path) {
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

function reorderArray(arr, fromIndex, toIndex) {
  const result = [...arr];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return result;
}

function configReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_PATH':
      return deepSet(state, action.path, action.value);
    case 'SET_LANGUAGE':
      return deepSet(state, 'meta.lang', action.lang);
    case 'REORDER': {
      const arr = deepGet(state, action.path);
      if (!Array.isArray(arr)) return state;
      const reordered = reorderArray(arr, action.fromIndex, action.toIndex);
      return deepSet(state, action.path, reordered);
    }
    case 'ADD_BLOCK': {
      const blocksPath = `pages.${action.pageKey}.blocks`;
      const currentBlocks = deepGet(state, blocksPath) || [];
      const newBlocks = [...currentBlocks];
      newBlocks.splice(action.insertIndex, 0, action.block);
      return deepSet(state, blocksPath, newBlocks);
    }
    case 'REMOVE_BLOCK': {
      const rmPath = `pages.${action.pageKey}.blocks`;
      const rmBlocks = deepGet(state, rmPath) || [];
      return deepSet(state, rmPath, rmBlocks.filter(b => b.id !== action.blockId));
    }
    case 'DUPLICATE_BLOCK': {
      const dupPath = `pages.${action.pageKey}.blocks`;
      const dupBlocks = deepGet(state, dupPath) || [];
      const dupIndex = dupBlocks.findIndex(b => b.id === action.blockId);
      if (dupIndex === -1) return state;
      const original = dupBlocks[dupIndex];
      const duplicate = {
        ...original,
        id: `block_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        props: { ...original.props },
        style: { ...original.style },
      };
      const dupNew = [...dupBlocks];
      dupNew.splice(dupIndex + 1, 0, duplicate);
      return deepSet(state, dupPath, dupNew);
    }
    case 'MOVE_BLOCK': {
      const movePath = `pages.${action.pageKey}.blocks`;
      const moveBlocks = deepGet(state, movePath);
      if (!Array.isArray(moveBlocks)) return state;
      return deepSet(state, movePath, reorderArray(moveBlocks, action.fromIndex, action.toIndex));
    }
    case 'ADD_NAV_ITEM': {
      const navItems = deepGet(state, 'sidebar.navItems') || [];
      return deepSet(state, 'sidebar.navItems', [...navItems, action.item]);
    }
    case 'REMOVE_NAV_ITEM': {
      const navArr = deepGet(state, 'sidebar.navItems') || [];
      const filtered = [...navArr];
      filtered.splice(action.index, 1);
      return deepSet(state, 'sidebar.navItems', filtered);
    }
    case 'UPDATE_I18N': {
      const langData = { ...state.i18n[action.lang] };
      langData[action.key] = action.value;
      return { ...state, i18n: { ...state.i18n, [action.lang]: langData } };
    }
    case 'SET_CONFIG':
      return action.config;
    case 'RESET':
      return defaultConfig;
    default:
      return state;
  }
}

function clearAdminConfig() {
  try {
    localStorage.removeItem(ADMIN_CONFIG_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * Returns the saved config only when it is safe to boot from. Any rejection
 * drops the stored copy so the next reload starts from the bundled default
 * instead of white-screening again.
 */
function loadAdminConfig() {
  let saved;
  try {
    const raw = localStorage.getItem(ADMIN_CONFIG_KEY);
    if (!raw) return null;
    saved = JSON.parse(raw);
  } catch {
    clearAdminConfig();
    return null;
  }

  if (!isValidConfig(saved)) {
    console.warn('[SiteConfig] Saved config has an unusable shape — discarding it.');
    clearAdminConfig();
    return null;
  }

  if (saved.schemaVersion !== defaultConfig.schemaVersion) {
    console.warn('[SiteConfig] Saved config was written for another schema version — discarding it.');
    clearAdminConfig();
    return null;
  }

  return saved;
}

function initConfig() {
  const saved = loadAdminConfig();
  return saved ? mergeWithDefaults(saved) : defaultConfig;
}

const historyReducer = createHistoryReducer(configReducer);

export function SiteConfigProvider({ children }) {
  const [state, dispatch] = useReducer(
    historyReducer,
    undefined,
    () => initialHistory(initConfig())
  );

  const history = useMemo(() => ({
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    undo: () => dispatch({ type: 'UNDO' }),
    redo: () => dispatch({ type: 'REDO' }),
    clearHistory: () => dispatch({ type: 'CLEAR_HISTORY' }),
  }), [state.past.length, state.future.length]);

  return (
    <SiteConfigContext.Provider value={state.present}>
      <SiteConfigDispatchContext.Provider value={dispatch}>
        <SiteConfigHistoryContext.Provider value={history}>
          {children}
        </SiteConfigHistoryContext.Provider>
      </SiteConfigDispatchContext.Provider>
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}

export function useSiteConfigDispatch() {
  return useContext(SiteConfigDispatchContext);
}

export function useSiteConfigHistory() {
  return useContext(SiteConfigHistoryContext);
}

export { deepGet, isValidConfig, deepMerge, mergeWithDefaults, ADMIN_CONFIG_KEY };
